import clsx from "classnames";
import React from "react";

import { Check, Minus } from "lucide-react";

import sharedStyles from "../../styles/shared/inputs.module.css";
import styles from "./index.module.css";

interface CheckboxProps extends Omit<React.ComponentProps<"input">, "checked"> {
  /** Id used for htmlFor label and input */
  id?: string;
  /** If checkbox is checked */
  isChecked?: boolean;
  /** If checkbox is disabled */
  isDisabled?: boolean;
  /** If checkbox is in intermediate/indeterminate state (mixed selection) */
  isIntermediate?: boolean;
  /** Label for the checkbox, can contain HTML */
  children?: React.ReactNode;
  /** Change handler for the checkbox */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** ID(s) of elements that describe this checkbox (for error messages, help text, etc.) */
  "aria-describedby"?: string;
  /** Indicates if the checkbox has a validation error */
  "aria-invalid"?: boolean;
  /** Indicates if the checkbox is required */
  "aria-required"?: boolean;
}

/**
 * Standard checkbox with label surrounding text and input element.
 */

const Checkbox: React.FC<CheckboxProps> = ({
  className,
  id,
  isChecked = false,
  isDisabled = false,
  isIntermediate = false,
  children,
  onChange,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  ...rest
}) => {
  const classes = clsx(
    styles.label,
    {
      [sharedStyles.isDisabled]: isDisabled,
      [styles.isChecked]: isChecked,
      [styles.isIntermediate]: isIntermediate,
    },
    className
  );

  const inputClasses = clsx(styles.hiddenInput, sharedStyles.hiddenInput);

  const iconElement = isIntermediate ? (
    <Minus size={16} />
  ) : (
    isChecked && <Check size={16} />
  );

  const inputRef = (input: HTMLInputElement | null) => {
    if (input) input.indeterminate = isIntermediate;
  };

  return (
    <label className={classes} htmlFor={id}>
      <input
        className={inputClasses}
        id={id}
        type="checkbox"
        disabled={isDisabled}
        checked={isChecked}
        onChange={onChange}
        ref={inputRef}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        {...rest}
      />
      <div className={styles.inputImage}>{iconElement}</div>
      {children && <div className={styles.labelText}>{children}</div>}
    </label>
  );
};

export default Checkbox;
